import { ProductPageData } from "../_types";

export const JSON_PRODUCT_MAJIK_SIGNATURE: ProductPageData = {
  id: "majik-signature",
  timestamp: new Date("2026-03-16").toISOString(),
  lastUpdate: new Date("2026-10-01").toISOString(), // TODO: confirm exact ship date of v0.11 against the changelog — set to today pending confirmation
  version: "0.17.0",
  isEnabled: true,
  repository: {
    url: "https://github.com/Majikah/majik-signature-desktop-releases",
    owner: "Majikah",
    repo: "majik-signature-desktop-releases",
  },
  metadata: {
    productName: "Majik Signature",
    productSlug: "majik-signature",
    tagline:
      "Free Digital Signing & File Verification with Post-Quantum Security",
    description:
      "Majik Signature is a free digital signing and verification app for documents, PDFs, Office files, images, audio, video, source code, and archives. It uses hybrid Ed25519 + ML-DSA-87 post-quantum cryptography to help prove file authenticity, integrity, and signer identity. Sign and verify locally without uploading files or creating an account for core offline use. Add Trusted Timestamps, signature expiration, visual and audio stamps, multi-party signing, revision-aware verification, and optional Solana Mainnet anchoring with Majik Notary.",

    seo: "Free digital signing and verification for documents, PDFs, media, code, and more. Majik Signature uses hybrid Ed25519 + ML-DSA-87 post-quantum cryptography.",

    keywords: [
      "digital-signatures",
      "post-quantum",
      "ml-dsa-87",
      "ed25519",
      "fips-204",
      "content-signing",
      "file-signing",
      "pdf-signing",
      "audio-signing",
      "video-signing",
      "cryptography",
      "authentication",
      "tamper-proof",
      "verification",
      "quantum-resistant",
      "hybrid-crypto",
      "seed-phrase",
      "bip39",
      "majikah",
      "majik-universal-id",
      "identity-signing",
      "visual-stamping",
      "trusted-timestamps",
      "tsa",
      "multi-signature",
      "batch-signing",
      "tauri",
      "signature-expiration",
      "majik-notary",
      "solana",
      "blockchain-anchoring",
      "revision-tracking",
      "chain-verification",
      "majik-slink",
    ],
    category: "Security",
    subcategory: "Digital Signatures & Content Authentication",
    photo: {
      thumbnail:
        "https://storage.majikah.solutions/public/Logo_MajikSignature_Majikah_Thumbnail_WEBP.webp",
      banner:
        "https://storage.majikah.solutions/public/MajikSignature_SmallTile.jpg",
      screenshots: [
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-1.png",
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-5.png",
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-6.png",
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-7.png",
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-10.png",
      ],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikSignature_SocialCard.webp",
    },
    video: ["f5oKQMnIpk8"],
  },

  // ============================================================
  // HERO — slide 1: split headline + live desktop-app screenshot,
  // with a short icon+label checklist under the copy.
  // ============================================================
  hero: {
    title: "Majik Signature",
    subtitle: "Hybrid Post-Quantum File Signing",
    description:
      "Sign virtually any file — documents, PDFs, Office files, images, audio, video, source code, and archives — to prove authenticity, integrity, and ownership. Add visual stamps, obtain Trusted Timestamps, set signature expiration dates, anchor sealed files on Solana Mainnet with Majik Notary, and run revision-aware multi-party signing workflows with full chain verification. Everything happens locally on your device — no account or internet connection required for core signing and verification.",
    ctaButtons: [
      {
        label: "Open Web App",
        url: "https://signature.majikah.solutions",
        variant: "primary",
      },
      {
        label: "Download for Windows/Linux",
        url: "/products/majik-signature/download",
        variant: "primary",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-signature",
        variant: "secondary",
        external: true,
      },
      {
        label: "See Technical Details",
        url: "#technical-specs",
        variant: "tertiary",
      },
      {
        label: "Read Docs",
        url: "/products/majik-signature/docs",
        variant: "tertiary",
      },
    ],
    badges: [
      {
        icon: "shield",
        label: "Hybrid Post-Quantum",
        description: "Ed25519 + ML-DSA-87",
      },
      {
        icon: "clock",
        label: "Trusted Timestamp",
        description: "Independently signed TSA proof of signing time",
      },
      {
        icon: "users",
        label: "Multi-Signing Workflows",
        description: "Allowlists, progress tracking, and sealing",
      },
      {
        icon: "link",
        label: "Majik Notary",
        description: "Solana Mainnet anchoring, no wallet required",
      },
      {
        icon: "clockCounterClockwise",
        label: "Full Chain Verification",
        description: "Revision-aware signatures, verified end to end",
      },
    ],
    mockup: {
      type: "mockupFrame",
      frame: "desktopApp",
      image:
        "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-1.png",
      alt: "Majik Signature desktop app showing a multi-signature PDF signing result",
      caption:
        "Advanced Mode — multi-party signing with a Trusted Timestamp applied",
      annotations: [
        { x: 27, y: 18, label: "Signing options & expected signers" },
        {
          x: 88,
          y: 47,
          label: "Full signature result: fingerprint, algorithm, TSA proof",
        },
      ],
    },
    scene3d: {
      type: "scene3d",
      sceneKind: "keyRing",
      caption:
        "A hybrid Ed25519 + ML-DSA-87 keypair, rendered as two interlocking rings",
      intensity: "subtle",
    },
  },

  // ============================================================
  // OVERVIEW — headline numbers as a stat grid instead of only prose.
  // ============================================================
  overview: {
    title: "What is Majik Signature?",
    paragraphs: [
      "Majik Signature is a hybrid post-quantum digital signature application that proves content authenticity and detects tampering. Sign virtually any file — documents, PDFs, Office files, images, audio, video, source code, and archives — with combined Ed25519 + ML-DSA-87 cryptography. Signatures are embedded directly into supported file formats using native metadata containers, with a universal fallback format that preserves compatibility for any other file type.",
      "Every signature is produced by two independent algorithms: Ed25519 (classical, 128-bit security) and ML-DSA-87 (post-quantum, NIST FIPS-204, Category 5 security). Both must verify for the signature to be valid. This means no single algorithmic break — classical or quantum — is sufficient to forge a signature.",
      "Verification requires only the signer's public keys — no private key, account, or internet connection needed, making it safe to verify content anywhere, fully offline. Signed files remain fully usable: PDFs stay PDFs, videos keep playing, Office documents remain editable, and images open normally.",
      "Beyond core signing, Majik Signature supports audio & visual stamping, Trusted Timestamps (TSA), signature expiration, multi-party signing workflows, and batch processing of files and ZIP archives. It's also the cryptographic signing engine powering Majik Universal ID — every MajikUniversalID identity record is anchored with a hybrid Ed25519 + ML-DSA-87 signature over its core fields, and all content and file signing performed through a MajikUniversalID delegates directly to Majik Signature.",
      "As of v0.11.0, multi-signature documents preserve every signer's signature across later revisions — such as a new stamp or annotation — and Full Chain Verification lets anyone load the files from a signing chain to confirm the complete revision history matches expected bytes and signatures at every step. Sealed files can also be optionally anchored on Solana Mainnet through Majik Notary, recording only a SHA3-512 seal hash on-chain for independent proof of existence, with no wallet or blockchain account required.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "Hybrid Post-Quantum Security",
        description:
          "Ed25519 + ML-DSA-87 (FIPS-204). Both algorithms must verify — defense-in-depth against classical and quantum attacks.",
      },
      {
        icon: "file",
        label: "Native File Embedding",
        description:
          "Signatures embed into PDFs, Office files, images, audio, video, and more. Files remain fully usable — no sidecar files.",
      },
      {
        icon: "stamp",
        label: "Document Stamping",
        description:
          "Add professional signatures, seals, and branding to PDF, DOCX, XLSX, and other supported documents with customizable placement and styling.",
      },
      {
        icon: "image",
        label: "Image Stamping",
        description:
          "Add logos, watermarks, overlays, and branding directly to supported image files with flexible placement and transformation controls.",
      },
      {
        icon: "stamp",
        label: "Audio Stamping",
        description:
          "Embed producer tags, voice signatures, and audio watermarks into signed audio files using a professional multi-track audio editor.",
      },
      {
        icon: "clock",
        label: "Trusted Timestamps",
        description:
          "Obtain independently signed TSA timestamps that provide verifiable proof of when a file was cryptographically signed.",
      },
      {
        icon: "users",
        label: "Multi-Signature Collaboration",
        description:
          "Multiple people can independently sign the same file, with signing allowlists, open or restricted modes, and file sealing to finalize documents.",
      },
      {
        icon: "wifiSlash",
        label: "Local-First, Offline-Capable",
        description:
          "All core cryptographic operations happen locally on your device. No account needed for offline signing and verification — your files are never uploaded to a server.",
      },
      {
        icon: "fingerprint",
        label: "Powers Majik Universal ID",
        description:
          "Majik Signature is the signing engine behind every MajikUniversalID. Identity anchoring, content provenance, and file signing in the Majikah ecosystem all run through this library.",
      },
      {
        icon: "link",
        label: "Majik Notary — Solana Mainnet",
        description:
          "Anchor a sealed file's SHA3-512 seal hash on Solana Mainnet for ₱50.00 per file — no wallet needed, gas fees covered by Majikah.",
      },
      {
        icon: "clockCounterClockwise",
        label: "Full Chain Verification",
        description:
          "Verify a file's complete revision history — each revision checked against its expected predecessor's bytes and signatures.",
      },
      {
        icon: "clock",
        label: "Signature Expiration",
        description:
          "Set an optional validity period on a signature — verification reports expired signatures as a distinct outcome from tampering.",
      },
    ],
    blocks: [
      {
        type: "statGrid",
        title: "At a glance",
        columns: 4,
        stats: [
          {
            icon: "shield",
            label: "Classical security",
            value: "128-bit",
            description: "Ed25519",
            colorKey: "accent",
          },
          {
            icon: "shieldCheck",
            label: "Post-quantum security",
            value: "Category 5",
            description: "ML-DSA-87 · NIST FIPS-204",
            colorKey: "primary",
          },
          {
            icon: "clock",
            label: "Free Trusted Timestamps",
            value: "5 / day",
            description: "Per Majikah account, every 24 hours",
            colorKey: "green",
          },
          {
            icon: "wifiSlash",
            label: "Account required for offline use",
            value: "None",
            description: "Sign and verify fully offline",
            colorKey: "blue",
          },
        ],
      },
      {
        type: "statGrid",
        title: "New in v0.11",
        columns: 3,
        stats: [
          {
            icon: "link",
            label: "Majik Notary anchoring",
            value: "₱50.00 / file",
            description: "Solana Mainnet, no wallet required",
            colorKey: "primary",
          },
          {
            icon: "users",
            label: "Multi-signatures across revisions",
            value: "Preserved",
            description: "Stamps and edits no longer invalidate prior signers",
            colorKey: "accent",
          },
          {
            icon: "checkCircle",
            label: "Signing chain verification",
            value: "Full history",
            description: "Every revision checked against its predecessor",
            colorKey: "green",
          },
        ],
      },
    ],
  },

  // ============================================================
  // FEATURES
  // ============================================================
  features: [
    {
      id: "hybrid-dual-algorithm",
      title: "Hybrid Dual-Algorithm Signatures",
      description:
        "Every Majik Signature is produced by two independent cryptographic algorithms signing the same canonical payload. This provides defense-in-depth: a classical attacker breaking Ed25519 cannot forge the ML-DSA-87 signature, and a quantum attacker breaking ML-DSA-87 cannot forge the Ed25519 signature.",
      icon: "shield",
      content: [
        "Ed25519 provides 128-bit classical security with a 64-byte signature — trusted and battle-tested.",
        "ML-DSA-87 (NIST FIPS-204, Category 5) provides post-quantum security equivalent to AES-256 with a 4595-byte signature.",
        "Both algorithms sign the same canonical payload: a domain-separated string binding the signer ID, timestamp, content type, and SHA-256 content hash.",
        "Verification requires both to pass — no single algorithmic break is sufficient to produce a valid forgery.",
        "File modifications immediately invalidate both signatures, providing tamper-evident guarantees.",
        "Majik Universal ID uses this exact hybrid scheme to anchor every identity record — signContent() and signFile() on a MajikUniversalID both delegate to MajikSignature.sign() and MajikSignature.signFile() respectively.",
      ],
      table: {
        headers: [
          "Algorithm",
          "Type",
          "Security Level",
          "Signature Size",
          "Public Key Size",
        ],
        rows: [
          {
            cells: [
              "Ed25519",
              "Classical (ECC)",
              "128-bit",
              "64 bytes",
              "32 bytes",
            ],
          },
          {
            cells: [
              "ML-DSA-87",
              "Post-Quantum (Lattice)",
              "Category 5 (~AES-256 PQ)",
              "4595 bytes",
              "2592 bytes",
            ],
          },
        ],
      },
      blocks: [
        {
          type: "paragraph",
          text: "Every Majik Signature is produced by two independent cryptographic algorithms signing the same canonical payload — defense-in-depth against both classical and quantum attacks.",
        },
        {
          type: "comparisonChart",
          chartType: "bar",
          title: "Signature & key size by algorithm",
          categoryKey: "algorithm",
          caption:
            "ML-DSA-87's larger footprint buys post-quantum resistance; Ed25519 keeps the envelope lean for everyday signing.",
          data: [
            { algorithm: "Ed25519", signatureBytes: 64, publicKeyBytes: 32 },
            {
              algorithm: "ML-DSA-87",
              signatureBytes: 4595,
              publicKeyBytes: 2592,
            },
          ],
          series: [
            {
              name: "Signature size (bytes)",
              dataKey: "signatureBytes",
              colorKey: "primary",
            },
            {
              name: "Public key size (bytes)",
              dataKey: "publicKeyBytes",
              colorKey: "accent",
            },
          ],
        },
        {
          type: "table",
          table: {
            headers: [
              "Algorithm",
              "Type",
              "Security Level",
              "Signature Size",
              "Public Key Size",
            ],
            rows: [
              {
                cells: [
                  "Ed25519",
                  "Classical (ECC)",
                  "128-bit",
                  "64 bytes",
                  "32 bytes",
                ],
              },
              {
                cells: [
                  "ML-DSA-87",
                  "Post-Quantum (Lattice)",
                  "Category 5 (~AES-256 PQ)",
                  "4595 bytes",
                  "2592 bytes",
                ],
              },
            ],
          },
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Both algorithms sign the same canonical, domain-separated payload.",
            },
            {
              text: "Verification requires both to pass — no single algorithmic break is sufficient.",
            },
            {
              text: "File modifications immediately invalidate both signatures.",
            },
            {
              text: "Majik Universal ID uses this exact hybrid scheme to anchor every identity record.",
            },
          ],
        },
      ],
    },
    {
      id: "majik-notary",
      title: "Majik Notary — Solana Mainnet Anchoring",
      description:
        "New in v0.11.0: Anchor a sealed file's cryptographic seal hash on Solana Mainnet for independent, on-chain proof of existence — without ever uploading the file itself. No crypto wallet or blockchain account required; Majikah covers the on-chain gas fees.",
      icon: "link",
      content: [
        "Available for any sealed file — documents, photos, videos, audio, applications, and software releases.",
        "Only the file's SHA3-512 seal hash is recorded on-chain via a Solana Memo — the file contents are never uploaded or exposed.",
        "No crypto wallet setup, no blockchain account, and no gas fees to manage — Majikah pays on-chain transaction costs through its internal system.",
        "Pay-per-file pricing: ₱50.00 per file (VAT exclusive; processing fees may apply).",
        "Notary anchoring is optional and additive — files remain fully verifiable offline via their cryptographic signatures with or without a notary anchor.",
        "Anchoring requires the file to be sealed first, so on-chain proof always corresponds to a finalized, tamper-evident signing state.",
      ],
      blocks: [
        {
          type: "statusCard",
          status: "valid",
          title: "Anchored on Solana Mainnet",
          subtitle: "Majik Notary",
          checks: [
            "SHA3-512 seal hash recorded via Solana Memo",
            "No wallet or blockchain account required",
            "Gas fees covered by Majikah",
          ],
          footerLabel: "₱50.00 per file",
        },
        {
          type: "paragraph",
          text: "Majik Notary gives sealed files an independent, on-chain timestamp of existence on Solana Mainnet — complementing (not replacing) the file's own hybrid post-quantum signature.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Anchors only the SHA3-512 seal hash — never the file itself.",
            },
            {
              text: "No wallet, blockchain account, or gas management needed.",
            },
            {
              text: "₱50.00 per file (VAT exclusive; processing fees may apply).",
            },
            {
              text: "Works with virtually any sealed file type.",
            },
          ],
        },
      ],
    },
    {
      id: "revision-aware-multisig",
      title: "Revision-Aware Multi-Signatures",
      description:
        "New in v0.11.0: Multi-signature workflows now preserve and validate signatures across file revisions. Drawing over a signed document, or re-stamping a signed photo or audio file, no longer invalidates earlier signers' signatures.",
      icon: "users",
      content: [
        "Each new revision of a file is cryptographically linked to the version that preceded it, forming a verifiable version chain.",
        "Prior signers' signatures remain valid and independently checkable even after later revisions (e.g. new stamps, new signers) are added.",
        "A version chain hash binds the revision history together, so metadata tampering is detectable even without any change to file content bytes.",
        "Two-tier verification: a self-contained check using only the latest file, and a deeper archive-assisted check when prior revisions are available.",
        "Designed for real-world workflows where a document is drawn on, a photo is stamped, or an audio file is remixed after being signed by one or more parties.",
      ],
      blocks: [
        {
          type: "paragraph",
          text: "Revision-aware multi-signatures mean a document can be signed, stamped, signed again, and revised further — without silently invalidating any prior signer's work.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Each revision cryptographically links back to the version before it.",
            },
            {
              text: "Prior signatures remain valid and verifiable after later revisions.",
            },
            {
              text: "A version chain hash detects metadata tampering across the revision history.",
            },
            {
              text: "Supports documents, stamped photos, and stamped/remixed audio.",
            },
          ],
        },
      ],
    },
    {
      id: "full-chain-verification",
      title: "Full Chain Verification",
      description:
        "New in v0.11.0: Load the files from a signing chain to verify the complete revision history — not just the latest version. Majik Signature checks that each revision matches the expected previous file's bytes and signatures.",
      icon: "checkCircle",
      content: [
        "The latest version of a file carries the complete chain information and all signatures by default, so the entire signing history can be verified starting from just that file.",
        "For a deeper check, load earlier revisions from the signing chain — Majik Signature confirms each revision matches the expected previous file's bytes and signatures.",
        "Detects gaps, substitutions, or out-of-order tampering anywhere in the revision history, not just at the most recent step.",
        "Complements revision-aware multi-signatures: signing history stays intact, and that history can itself be independently verified.",
      ],
      blocks: [
        {
          type: "stepTracker",
          title: "Full Chain Verification",
          orientation: "vertical",
          steps: [
            {
              icon: "file",
              title: "Load the Latest File",
              description:
                "The latest version carries the full chain information and all signatures by default.",
            },
            {
              icon: "clockCounterClockwise",
              title: "Load Prior Revisions (Optional)",
              description:
                "For archive-assisted verification, load earlier files from the signing chain.",
            },
            {
              icon: "checkCircle",
              title: "Verify Each Revision",
              description:
                "Each revision is checked against the expected previous file's bytes and signatures.",
            },
          ],
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Verify the complete revision history, not just the latest signature.",
            },
            {
              text: "The latest file is self-contained: full chain info and all signatures included by default.",
            },
            {
              text: "Archive-assisted mode checks each revision against its expected predecessor.",
            },
          ],
        },
      ],
    },
    {
      id: "signature-expiration",
      title: "Signature Expiration",
      description:
        "New in v0.10.0: Give a signature a defined validity period. Set an expiration date when signing, and anyone verifying the file can see whether the signature is still valid or has expired.",
      icon: "clock",
      content: [
        "Set an optional expiration date (validUntil) at signing time — ideal for time-sensitive contracts, approvals, credentials, and licenses.",
        "Verification automatically detects and reports expired signatures as a distinct outcome from tampered or invalid signatures.",
        "Expiry is checked only after both the Ed25519 and ML-DSA-87 signatures pass — a forged signature is never misreported as merely 'expired'.",
        "Expiration is part of the signed canonical payload, so the expiry date itself cannot be altered after signing without invalidating the signature.",
        "Leaving expiration unset means the signature does not expire — expiration is fully optional and opt-in.",
      ],
      blocks: [
        {
          type: "statusCard",
          status: "invalid",
          title: "EXPIRED",
          subtitle: "Signature valid until Jun 30, 2026",
          checks: [
            "Document is authentic",
            "File is unmodified",
            "Both signatures are cryptographically valid",
            "Validity period has passed",
          ],
          footerLabel:
            "Expired — signed correctly, but no longer within its validity window",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Optional expiration date set at signing time.",
            },
            {
              text: 'Verification reports "expired" as a distinct result from "invalid" or "tampered".',
            },
            {
              text: "Expiry checks only run after both signatures cryptographically pass.",
            },
            {
              text: "Ideal for contracts, approvals, credentials, and temporary licenses.",
            },
          ],
        },
      ],
    },
    {
      id: "document-stamping",
      title: "Document Stamping",
      description:
        "New in v0.4.0: Add professional visual stamps to signed documents — imported images or hand-drawn marks — for PDF, DOCX, and XLSX files. Stamps are a visual complement to the underlying cryptographic signature, not a replacement for it.",
      icon: "stamp",
      content: [
        "Import an image to use as a stamp, or draw one directly with a mouse or stylus.",
        "Chroma key support automatically removes a solid background color from imported stamp images.",
        "Resize, rotate, and reposition stamps freely on the page or sheet.",
        "Add optional name and position text alongside the stamp, with customizable font size and color.",
        "Supported in PDF, DOCX, and XLSX files.",
        "The visual stamp is layered on top of the document; the underlying cryptographic signature still covers the original file content and is what verification actually checks.",
      ],
      blocks: [
        {
          type: "mockupFrame",
          frame: "desktopApp",
          image:
            "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-5.png",
          alt: "Draw or import a visual stamp and place it on a signed document",
          caption:
            "Draw a stamp, or import PNG/JPG — chroma key removes the background automatically",
          annotations: [
            {
              x: 14,
              y: 62,
              label: "Draw with mouse or stylus, then place on the page",
            },
            {
              x: 76,
              y: 30,
              label:
                "Stamp is a visual layer — the signature still covers original bytes",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Stamps are a visual complement to the underlying cryptographic signature, not a replacement for it — the signature always covers the original document content.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Import an image or draw one directly with a mouse or stylus.",
            },
            {
              text: "Chroma key automatically removes a solid background color from imported images.",
            },
            {
              text: "Resize, rotate, and reposition freely; add optional name/position text.",
            },
            { text: "Supported in PDF, DOCX, and XLSX files." },
          ],
        },
      ],
    },
    {
      id: "audio-stamping",
      title: "Audio Stamping",
      description:
        "New in v0.5.0: Embed producer tags, voice signatures, or audio watermarks directly into signed audio files. Mix multiple audio stamps with professional editing controls while preserving the underlying cryptographic signature.",
      icon: "audio",
      content: [
        "Import one or more audio clips to use as reusable audio stamps.",
        "Create layered producer tags, voice signatures, or branded audio watermarks using a multi-track editor.",
        "Adjust per-track volume, pan, pitch, EQ, trimming, looping, and timeline placement with intuitive controls.",
        "Save and reload audio stamp presets for consistent branding across multiple projects.",
        "Supported for WAV and other compatible audio formats.",
        "The embedded audio stamp complements the underlying cryptographic signature, which remains the authoritative proof of file authenticity and ownership.",
      ],
      blocks: [
        {
          type: "mockupFrame",
          frame: "desktopApp",
          image:
            "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-Audio.png",
          alt: "Multi-track Audio Stamping editor in Majik Signature",
          caption:
            "Layer producer tags, voice signatures, and audio watermarks with professional multi-track controls",
          annotations: [
            {
              x: 18,
              y: 34,
              label: "Import and arrange multiple audio stamps on a timeline",
            },
            {
              x: 73,
              y: 64,
              label:
                "Adjust volume, pan, pitch, EQ, trim, looping, and track timing",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Audio Stamping lets you embed recognizable producer tags, voice signatures, and branded audio watermarks directly into your signed audio files. Like Visual Stamping, it enhances recognition while the embedded post-quantum cryptographic signature remains the authoritative proof of authenticity.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Import multiple audio clips as reusable producer tags, voice signatures, or audio watermarks.",
            },
            {
              text: "Professional multi-track editor with per-track volume, pan, pitch, EQ, solo, mute, trim, and looping.",
            },
            {
              text: "Zoom, stretch, resize, and precisely position tracks with keyboard and mouse shortcuts.",
            },
            {
              text: "Save reusable audio stamp presets to streamline future signing workflows.",
            },
            {
              text: "Supports WAV and other compatible audio formats.",
            },
          ],
        },
      ],
    },
    {
      id: "image-stamping",
      title: "Image Stamping",
      description:
        "New in v0.5.1: Add logos, watermarks, overlays, and branding directly to supported image files while preserving the underlying cryptographic signature.",
      icon: "image",
      content: [
        "Stamp images with logos, branding, signatures, or watermarks.",
        "Apply the same image stamp to multiple files using Batch Image Stamping.",
        "Smart Anchor Placement automatically scales and positions stamps consistently across different image sizes and aspect ratios.",
        "Customize stamp size, placement, and transformation with flexible presets and manual controls.",
        "Supported on PNG, JPG, WebP, and other compatible image formats.",
        "Image stamps visually enhance your files while the underlying cryptographic signature remains the authoritative proof of authenticity and ownership.",
      ],
      blocks: [
        {
          type: "mockupFrame",
          frame: "desktopApp",
          image:
            "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-Image.png",
          alt: "Image Stamping in Majik Signature",
          caption:
            "Add logos, watermarks, and branding with intelligent positioning across every image",
          annotations: [
            {
              x: 21,
              y: 36,
              label:
                "Import a logo or watermark and position it anywhere on the image",
            },
            {
              x: 76,
              y: 58,
              label:
                "Smart Anchor Placement keeps stamps aligned across different resolutions",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Image Stamping makes it easy to brand photos, artwork, renders, screenshots, and other supported images. Whether you're protecting your work or promoting your brand, Smart Anchor Placement ensures stamps remain consistently positioned regardless of image dimensions.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Add logos, watermarks, overlays, and branding directly to supported image files.",
            },
            {
              text: "Batch Image Stamping applies the same stamp across multiple images in one operation.",
            },
            {
              text: "Smart Anchor Placement automatically scales and positions stamps proportionally.",
            },
            {
              text: "Flexible resizing, placement presets, and manual transformation controls.",
            },
            {
              text: "Supports PNG, JPG, WebP, and other compatible image formats.",
            },
          ],
        },
      ],
    },
    {
      id: "trusted-timestamps",
      title: "Trusted Timestamps (TSA)",
      description:
        "New in v0.4.0: Obtain independently signed server timestamps that provide stronger proof of when a document was signed, using the same hybrid post-quantum cryptography as the rest of Majik Signature.",
      icon: "clock",
      content: [
        "TSA timestamps are issued and signed by an independent timestamp authority server, rather than being self-reported by the signer.",
        "TSA timestamps use the same hybrid Ed25519 + ML-DSA-87 cryptography as the core signing flow.",
        "Every Majikah account includes 5 free Trusted Timestamps every 24 hours.",
        "Re-signing the same unmodified file again within 24 hours does not consume an additional TSA credit.",
        "Additional TSA credit packs are available for purchase beyond the free daily allowance.",
        "Local (self-reported) timestamps remain the default and continue to work fully offline — TSA is optional and requires an internet connection and a Majikah account.",
      ],
      blocks: [
        {
          type: "statusCard",
          status: "valid",
          title: "Trusted Timestamp (TSA)",
          subtitle: "Issued by Majikah TSA",
          checks: [
            "Independently signed by tsa.majikah.solutions",
            "Hybrid Ed25519 + ML-DSA-87 algorithm",
            "Timestamped June 30, 2026, 12:48 PM UTC",
          ],
          footerLabel: "Verified",
        },
        {
          type: "paragraph",
          text: "TSA timestamps are issued and signed by an independent timestamp authority server rather than being self-reported by the signer, using the same hybrid cryptography as the core signing flow.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Every Majikah account includes 5 free Trusted Timestamps every 24 hours.",
            },
            {
              text: "Re-signing the same unmodified file again within 24 hours does not consume an additional credit.",
            },
            {
              text: "Additional TSA credit packs are available beyond the free daily allowance.",
            },
            {
              text: "Local (self-reported) timestamps remain the default and continue to work fully offline.",
            },
          ],
        },
      ],
    },
    {
      id: "multi-signature-collaboration",
      title: "Multi-Signature & Collaboration",
      description:
        "Multiple people can independently sign the same file while every individual signature is preserved — including across later revisions. Built for contracts, approvals, and any workflow that needs more than one signer.",
      icon: "users",
      content: [
        "Multi-party signing workflows allow several signers to add independent signatures to the same document over time.",
        "Signing allowlists restrict who is permitted to add a signature to a given document.",
        "Choose between open signing modes (anyone with the file can sign) or restricted modes (only allowlisted signers can sign).",
        "File sealing finalizes a signed document, preventing further signature changes once all required parties have signed.",
        "Progress tracking shows which signers are still pending and which have completed their signature.",
        "Every individual signature remains independently verifiable — a verifier can check each signer's signature separately.",
        "As of v0.11.0, signatures are preserved across file revisions — drawing over a document or re-stamping a photo or audio file no longer invalidates earlier signers' work.",
      ],
      blocks: [
        {
          type: "mockupFrame",
          frame: "desktopApp",
          image:
            "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-10.png",
          alt: "Restricted multi-signature verification showing 2 of 2 signers and per-signer detail",
          caption:
            "Advanced verification — every signer inspected individually, restricted allowlist mode",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Open signing (anyone with the file can sign) or restricted allowlist mode.",
            },
            {
              text: "Each signer's signature is added independently, without altering prior signers' signatures.",
            },
            { text: "Progress tracking shows pending vs. completed signers." },
            {
              text: "Seal individually or in bulk once all required signers have signed.",
            },
            {
              text: "Signatures survive later revisions — see Revision-Aware Multi-Signatures and Full Chain Verification.",
            },
          ],
        },
      ],
    },
    {
      id: "hot-folder-automation",
      title: "Hot Folder Automation",
      description:
        "Point a Hot Folder at Majik Signature and every incoming file is automatically verified and sorted into Passed or Failed — no technical setup required. Today, Hot Folders automate verification; automatic signing is on the roadmap.",
      icon: "folder",
      content: [
        "Create unlimited Hot Folders, each continuously monitored for new files.",
        "Every file dropped into a Hot Folder is automatically verified and sorted into a Passed or Failed subfolder based on the result.",
        "Custom Hot Folder icons make it easy to identify monitored folders at a glance in your file system.",
        "No manual verification step required — ideal for shared drives, intake folders, and team workflows where files arrive continuously.",
        "Hot Folders currently automate verification only; automatic signing of incoming files is planned but not yet available.",
      ],
      blocks: [
        {
          type: "paragraph",
          text: "Hot Folders remove the manual step of dragging files into the app one at a time — point a folder at Majik Signature and let it sort Passed from Failed automatically as files arrive.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Unlimited Hot Folders with continuous monitoring.",
            },
            {
              text: "Automatic Passed/Failed sorting with custom folder icons.",
            },
            {
              text: "Verification automation is live today; auto-signing of incoming files is on the roadmap.",
            },
          ],
        },
      ],
    },
    {
      id: "cli-mcp-automation",
      title: "Developer & AI Automation — CLI & MCP (Experimental)",
      description:
        "Automate signing, verification, and timestamping from scripts, CI/CD pipelines, or AI assistants and LLM agents via the CLI (mjksig) and MCP server (mjksig-mcp). Currently Windows-only and experimental — your private keys still never leave your device.",
      icon: "code",
      content: [
        "The mjksig command line interface supports signing, verification, timestamping, and sealing from scripts or CI/CD pipelines.",
        "The mjksig-mcp MCP server lets AI assistants and LLM agents invoke signing and verification as tools.",
        "Combine with Hot Folders for fully unattended verification pipelines.",
        "Both the CLI and MCP server are currently Windows-only and marked experimental — behavior and interfaces may change.",
        "Private keys never leave the device, even when signing is invoked via CLI or MCP.",
      ],
      blocks: [
        {
          type: "paragraph",
          text: "The CLI and MCP server extend Majik Signature into scripts, CI/CD pipelines, and AI agent workflows — currently Windows-only and experimental, with the same on-device key guarantees as the desktop app.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "mjksig CLI for signing, verification, timestamping, and sealing.",
            },
            {
              text: "mjksig-mcp MCP server for AI assistants and LLM agents.",
            },
            {
              text: "Windows-only and experimental today — scriptable and CI/CD-ready.",
            },
            {
              text: "Private keys never leave the device, regardless of invocation method.",
            },
          ],
        },
      ],
    },
    {
      id: "majik-slink",
      title: "Majik SLink — Sign Live URLs",
      description:
        "Cryptographically sign websites, social profiles, repositories, and other public pages. Publish an Ownership, Attribution, or Reference claim tied to your identity, and let anyone search and verify it at slink.majikah.solutions.",
      icon: "link",
      content: [
        "Sign live URLs — websites, social profiles, repositories, and other public pages — not just files.",
        "Choose from three claim types: Ownership, Attribution, or Reference, depending on your relationship to the content at that URL.",
        "Canonical URL normalization with DNS and content-challenge verification confirms the claim before it's published.",
        "Multiple independent, permanently shareable claims can exist per URL — different signers can each publish their own claim about the same page.",
        "Verification happens at slink.majikah.solutions and is independent of Majikah, but unlike Signature Envelope (file) verification, it is not fully offline — it requires reaching the SLink registry.",
        "The SLink registry is stored on Majikah's own database, not a DHT or federated system.",
      ],
      blocks: [
        {
          type: "paragraph",
          text: "Majik SLink extends Majik Signature's proof-of-authenticity model beyond files to live URLs — letting anyone verify who claims ownership, attribution, or reference to a public page.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Sign websites, social profiles, repositories, and other public URLs.",
            },
            {
              text: "Ownership, Attribution, or Reference claim types.",
            },
            {
              text: "DNS and content-challenge verification before a claim is published.",
            },
            {
              text: "Verification is independent but not offline — requires reaching slink.majikah.solutions.",
            },
            {
              text: "Multiple independent claims can be published per URL.",
            },
          ],
        },
      ],
    },
    {
      id: "file-embedding",
      title: "Native File Format Embedding",
      description:
        "Majik Signature embeds directly into each file format's native metadata container whenever possible. PDFs stay PDFs, WAVs stay WAVs, MP4s stay MP4s. A universal fallback format preserves compatibility for any other file type.",
      icon: "file",
      content: [
        "Tier 1 formats (PDF, PNG, JPEG, WAV, MP3, MP4, DOCX, etc.) use the format's built-in metadata. For example, PDF signatures are stored in the /Info dictionary and XMP metadata stream — visible in File → Properties.",
        "Tier 2 formats (any other file) use a universal self-describing binary trailer that can be cleanly stripped.",
        "Signing is safe and non-destructive — re-signing the same file strips the old signature and creates a new one without corrupting the file.",
        "The signature always covers the original file bytes before embedding. Verification strips the signature before re-hashing, so the round-trip is always: sign(originalBytes) → embed → extract → strip → verify(originalBytes).",
        "File format is auto-detected via magic bytes and MIME type sniffing — no manual hints required in most cases.",
      ],
      table: {
        headers: ["Format", "Tier", "Embedding Method", "Survives Re-save?"],
        rows: [
          {
            cells: [
              "PDF",
              "1",
              "/Info dict + XMP metadata",
              "Yes (most tools)",
            ],
          },
          { cells: ["PNG", "1", "iTXt chunk", "Yes"] },
          { cells: ["JPEG", "1", "APP15 marker", "Yes"] },
          { cells: ["WAV", "1", "RIFF LIST INFO chunk", "Yes"] },
          { cells: ["MP3", "1", "ID3v2 TXXX frame", "Yes"] },
          { cells: ["MP4/MOV", "1", "moov → udta → majk box", "Yes"] },
          { cells: ["DOCX/XLSX/PPTX", "1", "ZIP entry", "Yes"] },
          { cells: ["HTML/Markdown", "1", "Comment block", "Yes"] },
          {
            cells: [
              "Any other",
              "2",
              "Binary trailer",
              "No (re-encoding strips)",
            ],
          },
        ],
      },
      blocks: [
        {
          type: "tagMosaic",
          title: "Not just PDFs",
          description:
            "Sign virtually any file format while keeping it fully usable.",
          footerLabel: "One signer for your entire workflow.",
          tags: [
            { label: "PNG", colorKey: "primary" },
            { label: "JPG", colorKey: "accent" },
            { label: "GIF", colorKey: "green" },
            { label: "SVG", colorKey: "blue" },
            { label: "TIFF", colorKey: "red" },
            { label: "WEBP", colorKey: "primary" },
            { label: "PSD", colorKey: "accent" },
            { label: "DOCX", colorKey: "blue" },
            { label: "PDF", colorKey: "red" },
            { label: "PPTX", colorKey: "primary" },
            { label: "XLSX", colorKey: "green" },
            { label: "ODT", colorKey: "accent" },
            { label: "MP3", colorKey: "primary" },
            { label: "WAV", colorKey: "accent" },
            { label: "FLAC", colorKey: "green" },
            { label: "MP4", colorKey: "blue" },
            { label: "MOV", colorKey: "red" },
            { label: "MKV", colorKey: "primary" },
            { label: "JSON", colorKey: "blue" },
            { label: "CSV", colorKey: "green" },
            { label: "MD", colorKey: "neutral" },
            { label: "ZIP", colorKey: "red" },
            { label: "TAR.GZ", colorKey: "accent" },
            { label: "EPUB", colorKey: "primary" },
          ],
        },
        {
          type: "table",
          table: {
            headers: [
              "Format",
              "Tier",
              "Embedding Method",
              "Survives Re-save?",
            ],
            rows: [
              {
                cells: [
                  "PDF",
                  "1",
                  "/Info dict + XMP metadata",
                  "Yes (most tools)",
                ],
              },
              { cells: ["PNG", "1", "iTXt chunk", "Yes"] },
              { cells: ["JPEG", "1", "APP15 marker", "Yes"] },
              { cells: ["WAV", "1", "RIFF LIST INFO chunk", "Yes"] },
              { cells: ["MP3", "1", "ID3v2 TXXX frame", "Yes"] },
              { cells: ["MP4/MOV", "1", "moov → udta → majk box", "Yes"] },
              { cells: ["DOCX/XLSX/PPTX", "1", "ZIP entry", "Yes"] },
              { cells: ["HTML/Markdown", "1", "Comment block", "Yes"] },
              {
                cells: [
                  "Any other",
                  "2",
                  "Binary trailer",
                  "No (re-encoding strips)",
                ],
              },
            ],
          },
        },
      ],
    },
    {
      id: "identity-signing",
      title: "Identity-Bound Signing via Majik Universal ID",
      description:
        "Majik Signature is the cryptographic signing engine for Majik Universal ID. Every MajikUniversalID is permanently anchored with a hybrid Ed25519 + ML-DSA-87 identity signature, and all content and file signing through a MajikUniversalID delegates directly to this library.",
      icon: "fingerprint",
      content: [
        "When a MajikUniversalID is created, MajikSignature.sign() produces a hybrid identity signature over the canonical identity fields (id, user_id, timestamp, hash) — both Ed25519 and ML-DSA-87 must verify for the identity record to be authentic.",
        "majikId.signContent() calls MajikSignature.sign() under the hood — the result is a full MajikSignature instance carrying both algorithm signatures.",
        "majikId.signFile() calls MajikSignature.signFile() — the hybrid signature is embedded directly into the file's native metadata container.",
        "majikId.verifyContent() calls MajikSignature.verify() using the public keys from the bound MajikKey bundle — signer fingerprint binding is enforced before cryptographic verification.",
        "majikId.verifyFile() calls MajikSignature.verifyFile() — the embedded signature is extracted and both Ed25519 and ML-DSA-87 must pass.",
        "All signing operations on a MajikUniversalID require that the identity is at IDTier.UNVERIFIED (mutable) — verified identities are immutable and cannot add new signatures.",
      ],
    },
    {
      id: "seed-phrase-identity",
      title: "Seed Phrase Identity (Majik Key)",
      description:
        "Majik Signature uses your Majik Key account — a BIP-39 mnemonic seed phrase identity (12/24-word or 24-word). Your signing keys are deterministically derived from your seed phrase, so you can recover your full signing capability from your seed phrase on any device.",
      icon: "key",
      content: [
        "Your account is a BIP-39 mnemonic — the same standard used by cryptocurrency wallets — with full support for both 12/24-word and 24-word recovery phrases across every official BIP-39 language.",
        "Two signing keypairs are derived deterministically: Ed25519 (for the classical signature) and ML-DSA-87 (for the post-quantum signature).",
        "Private keys are encrypted at rest inside Majik Key, the identity engine, using Argon2id (m=65,536 KB / 64 MiB, t=3, p=4) and protected by your passphrase.",
        "As long as you have your seed phrase, you can recover your signing identity anywhere — no cloud backup required.",
        "No Majikah account is required for offline signing and verification. An optional Majikah account unlocks Trusted Timestamps and other online features.",
        "Majik Key is also the foundational identity layer for Majik Universal ID — the same key bundle (including X25519, Ed25519, ML-KEM-768, and ML-DSA-87 public keys) is committed into the MajikUniversalID integrity hash at creation time.",
      ],
      list: [
        {
          label: "Ed25519 keypair",
          text: "Derived from seed[0..32] → provides your fingerprint and classical signature capability",
        },
        {
          label: "ML-DSA-87 keypair",
          text: "Derived from full seed[0..64] → provides post-quantum signature capability",
        },
        {
          text: "Both keypairs are deterministic — same seed phrase always produces the same keys",
        },
        {
          text: "Private keys never leave your device and are never transmitted",
        },
      ],
    },
    {
      id: "windows-hello-passwordless",
      title: "Credential Store Passwordless Unlock",
      description:
        "Unlock your Majik Key instantly with Credential Store — face, fingerprint, or PIN — instead of typing a passphrase. Best-effort and fully additive: if Credential Store setup ever fails, your account keeps working through the normal passphrase flow.",
      icon: "lock",
      content: [
        "When you opt in, a cryptographically random 64-byte (128 hex character) passphrase is generated and fed into Argon2id via Majik Key, rather than a passphrase you type yourself.",
        "Credential Store guards access to that random secret — unlocking your account means authenticating with your face, fingerprint, or PIN.",
        "This combines Credential Store's biometric/PIN gate with Argon2id's memory-hard protection, removing human-guessable-passphrase risk for users who opt in.",
        "Passwordless unlock is additive and best-effort: if Credential Store setup or authentication fails, the account continues to work via the standard passphrase flow.",
        "Choose between Unlock Once (keys stay available for the session) or Unlock Per Operation (maximum security, re-authenticate for every signing action).",
      ],
      blocks: [
        {
          type: "paragraph",
          text: "Credential Store passwordless unlock replaces a typed passphrase with a random, high-entropy secret guarded by your device's biometric or PIN authentication — with the normal passphrase flow always available as a fallback.",
        },
        {
          type: "bulletList",
          variant: "check",
          items: [
            {
              text: "Auto-generated random 64-byte passphrase, never typed or memorized.",
            },
            {
              text: "Credential Store (face, fingerprint, or PIN) gates access to that secret.",
            },
            {
              text: "Additive and best-effort — falls back to normal passphrase flow if setup fails.",
            },
            {
              text: "Configurable Unlock Once vs. Unlock Per Operation behavior.",
            },
          ],
        },
      ],
    },
    {
      id: "sign-verify-workflow",
      title: "Guided or Advanced Signing & Verification",
      description:
        "New in v0.4.0: Choose a guided Signing Wizard for step-by-step signing, or the original Advanced Mode for full control. Verification offers a Quick Mode for fast checks and an Advanced Mode for detailed technical inspection.",
      icon: "checkCircle",
      content: [
        "Signing Wizard: a guided, step-by-step flow for signing files — recommended for most users and new signers.",
        "Advanced Mode (signing): the original full-control interface, including audio & visual stamping, TSA timestamp selection, signature expiration, and multi-signature options.",
        "Quick Mode (verification): fast drag-and-drop verification for a simple valid/invalid result.",
        "Advanced Mode (verification): detailed technical information, paginated signatures, multi-signer inspection, full chain verification, and flexible verification methods (contact-based or self-reported keys).",
        "Inline file previews for images, audio (waveform player), video, PDFs, and text files before signing.",
        "Account selector shows all your Majik Key accounts with signing capability. Warns if an account has no signing keys (needs re-import via mnemonic backup).",
        "New users can skip onboarding entirely as of v0.10.0 and go straight into the app.",
      ],
      blocks: [
        {
          type: "stepTracker",
          title: "Signing Wizard",
          orientation: "vertical",
          steps: [
            {
              icon: "file",
              title: "Select File",
              description: "Choose the file you want to sign.",
            },
            {
              icon: "users",
              title: "Choose Signers",
              description: "Open or restricted signing, allowlists.",
            },
            {
              icon: "sliders",
              title: "Configure Signing",
              description: "Stamp, TSA, expiration, and other options.",
            },
            {
              icon: "checkCircle",
              title: "Review & Sign",
              description: "Preview and confirm, then seal.",
            },
          ],
        },
        {
          type: "mockupFrame",
          frame: "desktopApp",
          image:
            "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-6.png",
          alt: "Signing Wizard walking through Choose File, Choose Signers, Configure Signing, Review and Sign",
          caption:
            "Wizard Mode — every step guided, full result detail on completion",
        },
      ],
    },
    {
      id: "tamper-detection",
      title: "Tamper Detection & Integrity Verification",
      description:
        "Any modification to signed content — even a single bit flip — invalidates both signatures. Majik Signature provides cryptographic proof that content has not been altered since signing.",
      icon: "warning",
      content: [
        "The SHA-256 content hash is part of the canonical payload signed by both algorithms.",
        "On verification, the content is re-hashed and compared to the hash in the signature envelope. If they don't match, verification fails immediately.",
        "Because the hash is signed, an attacker cannot replace the content and update the hash without re-signing with the private keys.",
        "This protects against accidental corruption (file transfer errors, disk corruption) and intentional tampering.",
        "For embedded signatures in files, the signature always covers the original file bytes before embedding, so the embedding process itself cannot be tampered with.",
        "For files with a revision history, a version chain hash also detects metadata-only tampering across revisions — see Full Chain Verification.",
      ],
      blocks: [
        {
          type: "statusCard",
          status: "valid",
          title: "VALID",
          subtitle: "2 signatures found",
          checks: [
            "Document is authentic",
            "File is unmodified",
            "All signatures are valid",
            "Trusted timestamp verified",
          ],
          footerLabel: "No account · No private key · Offline",
        },
        {
          type: "paragraph",
          text: "On verification, the content is re-hashed and compared to the hash in the signature envelope. If they don't match, verification fails immediately — because the hash itself is signed, an attacker cannot swap the content without re-signing with the private keys.",
        },
      ],
    },
    {
      id: "offline-operation",
      title: "Local-First, Offline-Capable",
      description:
        "Core cryptographic operations happen locally on your device. No account or internet connection is required to sign or verify files offline — your files are never uploaded to a server.",
      icon: "wifiSlash",
      content: [
        "Sign files and text without an internet connection, and without a Majikah account.",
        "Verify signatures offline — only the signer's public keys are needed.",
        "Private keys are stored encrypted in your browser's IndexedDB (web app) or local filesystem (desktop app).",
        "An internet connection is only required for optional online features: Trusted Timestamp (TSA) requests, Majik Notary anchoring, Majik Universal ID, Majik SLink registry, and Majikah account features.",
        "Perfect for air-gapped systems, high-security environments, or situations where network access is restricted or untrusted.",
        "Signatures can be exported as base64 strings and shared through any channel (email, USB, messaging apps).",
      ],
      blocks: [
        {
          type: "badgeRow",
          columns: 3,
          badges: [
            {
              icon: "lock",
              label: "Your Files Stay On Your Device",
              description:
                "Signing and verification happen locally — no uploads, no cloud dependency.",
            },
            {
              icon: "wifiSlash",
              label: "No Internet Required",
              description:
                "Sign and verify fully offline; TSA, Notary, and account features are optional.",
            },
            {
              icon: "shield",
              label: "Privacy by Design",
              description:
                "Private keys never leave your device and are never transmitted.",
            },
          ],
        },
      ],
    },
    {
      id: "multi-account",
      title: "Multi-Account Support",
      description:
        "Manage multiple cryptographic identities for different contexts. Switch between accounts seamlessly.",
      icon: "users",
      content: [
        "Store up to 100 Majik Key accounts locally in the web app or desktop app.",
        "Each account is a separate Majik Key with its own seed phrase, fingerprint, and signing keys.",
        "Perfect for separating personal, work, and project identities.",
        "Account selector shows all your signing-capable accounts with status indicators (unlocked, locked, no keys).",
        "Switch between accounts with one click — no logout/login required.",
      ],
    },
    {
      id: "contact-directory",
      title: "Contact Directory for Verification",
      description:
        "Import contacts (other people's Majik Key public keys) to verify their signatures. Contacts are stored locally and identified by cryptographic fingerprints. Store up to 5,000 contacts, organized by trusted signers and recipients.",
      icon: "addressBook",
      content: [
        "Contacts are identified by verified public key fingerprints (SHA-256 hashes of Ed25519 public keys).",
        "Contact cards include both Ed25519 and ML-DSA-87 public keys for hybrid signature verification.",
        "Store up to 5,000 contacts, organized by trusted signers and recipients.",
        "Share your contact information via invite keys — one-click generation and import.",
        "Verification can be done against a specific contact (trusted) or self-reported keys (TOFU — trust-on-first-use).",
        "No phone numbers, emails, or personal data required.",
      ],
    },
    {
      id: "activity-history",
      title: "Privacy-Friendly Activity History",
      description:
        "New in v0.9.1: A local log of what you've signed, verified, and sealed — stored as cryptographic digests rather than file names or content, with retention you control.",
      icon: "clockCounterClockwise",
      content: [
        "Every sign, verify, and seal action is recorded locally as a privacy-preserving log entry.",
        "Entries are stored as cryptographic digests, not file names or file contents.",
        "Configurable retention lets you control how long history entries are kept before automatic cleanup.",
        "History stays entirely on-device — it is never uploaded or synced to Majikah servers.",
      ],
    },
  ],

  // ============================================================
  // TECHNICAL SPECS
  // ============================================================
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Stack",
        description:
          "Majik Signature uses well-audited cryptographic primitives from the StableLib and Noble families. Argon2id-based key derivation protects private keys inside Majik Key, the identity engine that Majik Signature consumes — it is not a Majik Signature-native primitive.",
        table: {
          headers: ["Component", "Library", "Version", "Usage"],
          rows: [
            {
              cells: [
                "Ed25519",
                "@stablelib/ed25519",
                "^2.0.2",
                "Classical signature (64 bytes)",
              ],
            },
            {
              cells: [
                "ML-DSA-87 (FIPS-204)",
                "@noble/post-quantum",
                "^0.5.4",
                "Post-quantum signature (4595 bytes)",
              ],
            },
            {
              cells: [
                "SHA-256",
                "@stablelib/sha256",
                "^2.0.1",
                "Content hashing",
              ],
            },
            {
              cells: [
                "SHA3-512",
                "@noble/hashes",
                "^2.0.1",
                "Seal hash used for Majik Notary on-chain anchoring",
              ],
            },
            {
              cells: [
                "Argon2id (via Majik Key)",
                "hash-wasm + @noble/hashes",
                "^4.12.0 / ^2.0.1",
                "Passphrase-derived key protection inside Majik Key (m=65,536 KB, t=3, p=4) — consumed by Majik Signature, not implemented by it",
              ],
            },
            {
              cells: [
                "BIP-39",
                "@scure/bip39",
                "^1.6.0",
                "Mnemonic generation and seed derivation",
              ],
            },
          ],
        },
      },
      {
        title: "Signature Envelope Format",
        description:
          "Every MajikSignature serializes to a well-defined JSON structure containing both signatures, signer public keys, content hash, timestamp, optional expiration, revision-chain linkage, and metadata.",
        table: {
          headers: ["Field", "Type", "Size", "Description"],
          rows: [
            {
              cells: ["version", "integer", "—", "Envelope version (always 1)"],
            },
            {
              cells: [
                "signerId",
                "string",
                "44 chars",
                "Signer fingerprint (base64 SHA-256 of Ed25519 pubkey)",
              ],
            },
            {
              cells: [
                "signerEdPublicKey",
                "string",
                "44 chars",
                "Ed25519 public key (base64, 32 bytes)",
              ],
            },
            {
              cells: [
                "signerMlDsaPublicKey",
                "string",
                "~3456 chars",
                "ML-DSA-87 public key (base64, 2592 bytes)",
              ],
            },
            {
              cells: [
                "contentHash",
                "string",
                "44 chars",
                "SHA-256 of content (base64, 32 bytes)",
              ],
            },
            {
              cells: [
                "contentType",
                "string",
                "variable",
                "Advisory MIME type (e.g., 'audio/wav')",
              ],
            },
            {
              cells: [
                "timestamp",
                "string",
                "variable",
                "ISO 8601 signing timestamp (local or TSA-issued)",
              ],
            },
            {
              cells: [
                "validUntil",
                "string (optional)",
                "variable",
                "ISO 8601 expiration timestamp — signature is reported as expired after this point",
              ],
            },
            {
              cells: [
                "versionChainHash",
                "string (optional)",
                "44 chars",
                "SHA-256 hash binding this revision to its version chain, used for Full Chain Verification (vch)",
              ],
            },
            {
              cells: [
                "tsaToken",
                "string (optional)",
                "variable",
                "Independently signed TSA timestamp token, when a Trusted Timestamp is used",
              ],
            },
            {
              cells: [
                "notaryAnchor",
                "object (optional)",
                "variable",
                "Solana Mainnet Memo transaction reference for the sealed file's SHA3-512 seal hash, when Majik Notary is used",
              ],
            },
            {
              cells: [
                "edSignature",
                "string",
                "~88 chars",
                "Ed25519 signature (base64, 64 bytes)",
              ],
            },
            {
              cells: [
                "mlDsaSignature",
                "string",
                "~6127 chars",
                "ML-DSA-87 signature (base64, 4595 bytes)",
              ],
            },
          ],
        },
        blocks: [
          {
            type: "codeOrEnvelope",
            title: "Example signature envelope",
            language: "json",
            content: `{
  "version": 1,
  "signerId": "jUNOZgW9lHnWbJmXrS8nxRiv13HB3cNjrnAIzXlnA7c=",
  "contentHash": "V/IN5XbzFjeKI/tlYBu6STcokt+j7qOM9bSTfT7yM9g=",
  "contentType": "application/pdf",
  "timestamp": "2026-06-30T12:48:00Z",
  "validUntil": "2026-12-31T23:59:59Z",
  "versionChainHash": "8dFhX3s1QW1lYBu6STcokt+j7qOM9bSTfT7yM2z=",
  "tsaToken": "PTNvVMT2pCo48zcJRbrz5ybPTNCYlynuaI8exvz2EMM=",
  "edSignature": "base64(64 bytes)",
  "mlDsaSignature": "base64(4595 bytes)"
}`,
          },
        ],
      },
      {
        title: "Majik Notary — Solana Mainnet Anchoring",
        description:
          "Majik Notary anchors a sealed file's seal hash on Solana Mainnet using a Memo transaction, providing an independent, publicly verifiable on-chain timestamp without ever transmitting file contents.",
        table: {
          headers: ["Aspect", "Detail"],
          rows: [
            { cells: ["Network", "Solana Mainnet"] },
            {
              cells: [
                "On-chain data",
                "SHA3-512 seal hash only, via Solana Memo — file contents are never uploaded",
              ],
            },
            {
              cells: [
                "Wallet requirement",
                "None — no crypto wallet or blockchain account needed",
              ],
            },
            {
              cells: [
                "Gas fees",
                "Covered by Majikah through its internal system",
              ],
            },
            {
              cells: [
                "Prerequisite",
                "File must be sealed before it can be anchored",
              ],
            },
            {
              cells: [
                "Pricing",
                "₱50.00 per file (VAT exclusive; processing fees may apply)",
              ],
            },
          ],
        },
        blocks: [
          {
            type: "paragraph",
            text: "Notary anchoring is independent of, and additive to, Majik Signature's own hybrid post-quantum signatures — files remain fully verifiable offline whether or not they are anchored on-chain.",
          },
        ],
      },
      {
        title: "Usage in Majik Universal ID",
        description:
          "Majik Signature exposes the following surface area consumed by MajikUniversalID internally.",
        table: {
          headers: ["MajikUniversalID Method", "Delegates To", "Description"],
          rows: [
            {
              cells: [
                "create()",
                "MajikSignature.sign()",
                "Signs canonical identity fields (id, user_id, timestamp, hash) to produce the identity anchor signature",
              ],
            },
            {
              cells: [
                "signContent()",
                "MajikSignature.sign()",
                "Signs arbitrary content and returns a MajikSignature instance",
              ],
            },
            {
              cells: [
                "signFile()",
                "MajikSignature.signFile()",
                "Signs a file and embeds the hybrid signature into its native metadata container",
              ],
            },
            {
              cells: [
                "verifyContent()",
                "MajikSignature.verify()",
                "Verifies both Ed25519 and ML-DSA-87 against the bound public keys",
              ],
            },
            {
              cells: [
                "verifyFile()",
                "MajikSignature.verifyFile()",
                "Extracts and verifies the embedded signature from a signed file",
              ],
            },
            {
              cells: [
                "verifyText()",
                "MajikSignature.verify()",
                "Convenience wrapper for string content verification",
              ],
            },
          ],
        },
      },
      {
        title: "Platform Support",
        description:
          "Majik Signature is built with Tauri for a lightweight, fast, and secure desktop experience, alongside a web app and an npm package.",
        table: {
          headers: ["Platform", "Status", "Notes"],
          rows: [
            {
              cells: [
                "Web App",
                "✓ Available",
                "https://signature.majikah.solutions",
              ],
            },
            {
              cells: [
                "Windows Desktop",
                "✓ Available",
                "Microsoft Store (Tauri app)",
              ],
            },
            {
              cells: ["macOS Desktop", "⧗ Coming Soon", "Tauri app"],
            },
            {
              cells: [
                "Linux Desktop",
                "⧗ Coming Soon",
                "AppImage / .deb (Tauri app)",
              ],
            },
            { cells: ["Chrome Extension", "⧗ Planned", "Chrome Web Store"] },
            { cells: ["Mobile (iOS/Android)", "⧗ Planned", "React Native"] },
            {
              cells: [
                "npm package (@majikah/majik-signature)",
                "✓ Available",
                "Peer dependency for Majik Universal ID",
              ],
            },
            {
              cells: [
                "CLI (mjksig) / MCP Server (mjksig-mcp)",
                "✓ Available (Experimental)",
                "Windows-only today",
              ],
            },
          ],
        },
        blocks: [
          {
            type: "statGrid",
            columns: 4,
            stats: [
              {
                label: "Web App",
                value: "Available",
                icon: "globe",
                colorKey: "green",
              },
              {
                label: "Windows Desktop",
                value: "Available",
                icon: "desktop",
                colorKey: "green",
              },
              {
                label: "macOS / Linux",
                value: "Coming Soon",
                icon: "desktop",
                colorKey: "primary",
              },
              {
                label: "Mobile",
                value: "Planned",
                icon: "devices",
                colorKey: "neutral",
              },
            ],
          },
        ],
      },
    ],
  },

  // ============================================================
  // HOW IT WORKS — key processes rendered as a step tracker
  // (slide 6), with mockup / status card blocks attached.
  // ============================================================
  howItWorks: {
    title: "How Majik Signature Works",
    processes: [
      {
        title: "Creating a Majik Key Account",
        description:
          "Before signing, you need a Majik Key account. This is your cryptographic identity.",
        useStepTracker: true,
        steps: [
          {
            text: "Generate a BIP-39 mnemonic (12/24-word or 24-word, any official BIP-39 language) using cryptographically secure random number generation (CSPRNG)",
            type: "numbered",
          },
          {
            text: "The mnemonic is converted to a 64-byte seed via BIP-39 PBKDF2-SHA512",
            type: "numbered",
          },
          {
            text: "Two signing keypairs are deterministically derived: Ed25519 keypair from seed[0..32]; ML-DSA-87 keypair from seed[0..64]",
            type: "numbered",
          },
          {
            text: "Your Ed25519 public key is hashed (SHA-256) to create your account fingerprint",
            type: "numbered",
          },
          {
            text: "Both private keys are encrypted with your passphrase using Argon2id (m=65,536 KB / 64 MiB, t=3, p=4) inside Majik Key and stored locally — optionally guarded by Credential Store instead of a typed passphrase",
            type: "numbered",
          },
        ],
        note: "Your seed phrase and private keys never leave your device. The passphrase (or Credential Store, if enabled) protects your Argon2id-encrypted private keys in local storage.",
      },
      {
        title: "How Majik Universal ID Uses Majik Signature",
        description:
          "When a MajikUniversalID is created, Majik Signature is invoked to produce the identity anchor signature. This signature binds the identity record to a specific MajikKey and proves the record has not been tampered with.",
        steps: [
          {
            text: "MajikUniversalID.create() calls MajikSignature.sign() with the canonical identity payload: JSON({ id, user_id, timestamp, hash }) prefixed by the domain separator",
            type: "numbered",
          },
          {
            text: "Both Ed25519 and ML-DSA-87 sign this payload — the resulting MajikSignature envelope is stored in the identity's signature field",
            type: "numbered",
          },
          {
            text: "The MajikIDSignature record stores both algorithm signatures, the signer's Ed25519 and ML-DSA-87 public keys, the content hash, and the list of signed fields",
            type: "numbered",
          },
          {
            text: "When signContent() or signFile() is later called on the identity, MajikSignature.sign() or MajikSignature.signFile() is invoked with the bound key — the fingerprint is checked before any signing occurs",
            type: "numbered",
          },
          {
            text: "verifyContent() and verifyFile() extract the public keys from the bound MajikKeyPublicBundle and call MajikSignature.verify() or MajikSignature.verifyFile() — signer fingerprint binding is enforced",
            type: "numbered",
          },
        ],
        note: "All signing operations on a MajikUniversalID require that the identity is at IDTier.UNVERIFIED (isMutable === true). Verified identities are immutable and cannot add new signatures.",
      },
      {
        title: "Signing a File",
        description:
          "When you sign a file in Majik Signature, using either the guided Signing Wizard or Advanced Mode:",
        useStepTracker: true,
        steps: [
          {
            text: "Select your signing account and ensure it's unlocked (enter your passphrase, or authenticate with Credential Store if enabled)",
            type: "numbered",
          },
          {
            text: "If the file already has an embedded signature, it's automatically stripped before signing (safe, non-destructive re-signing) — or preserved as a linked revision if revision-aware signing is used",
            type: "numbered",
          },
          {
            text: "Optionally add a visual stamp, set a signature expiration date, or request a Trusted Timestamp (TSA) instead of a local timestamp",
            type: "numbered",
          },
          {
            text: "The file bytes are hashed with SHA-256, and a canonical payload is built binding signer, timestamp, expiration (if set), content type, and hash",
            type: "numbered",
          },
          {
            text: "Your Ed25519 private key and ML-DSA-87 secret key each sign this payload independently",
            type: "numbered",
          },
          {
            text: "Both signatures, your public keys, the content hash, timestamp, optional expiration, and content type are assembled into a MajikSignature JSON envelope",
            type: "numbered",
          },
          {
            text: "The envelope — and any visual stamp — is embedded into the file's native metadata container",
            type: "numbered",
          },
        ],
        note: "The signature always covers the original file bytes before embedding. Visual stamps are a presentation layer on top of the document and do not change what is cryptographically signed.",
        blocks: [
          {
            type: "mockupFrame",
            frame: "desktopApp",
            image:
              "https://storage.majikah.solutions/public/promo/MajikSignature_Store_PromoArtboard-6.png",
            alt: "File signed successfully, with full technical result panel",
            caption:
              "Sign & Seal — the final wizard step, with the complete signature result",
          },
        ],
      },
      {
        title: "Revising a Signed File",
        description:
          "When a signed file is later drawn on, re-stamped, or otherwise revised — for example, adding a stamp to a photo that's already been signed by one or more parties:",
        steps: [
          {
            text: "The new revision is hashed and a canonical payload is built that includes a link back to the prior revision",
            type: "numbered",
          },
          {
            text: "A version chain hash is computed, binding this revision to the full history of prior revisions",
            type: "numbered",
          },
          {
            text: "Prior signers' signatures remain embedded and untouched — the new revision's signature is added alongside them, not in place of them",
            type: "numbered",
          },
          {
            text: "The latest file carries the complete chain information and all signatures by default, so it can be verified on its own or against the full chain of prior files",
            type: "numbered",
          },
        ],
        note: "Revision-aware signing means drawing over a document or re-stamping a photo or audio file no longer invalidates earlier signers' work.",
      },
      {
        title: "Multi-Party Signing",
        description:
          "When a document needs more than one signer, Majik Signature supports collaborative signing workflows:",
        steps: [
          {
            text: "The initiating signer chooses an open signing mode or a restricted mode using a signing allowlist",
            type: "numbered",
          },
          {
            text: "Each subsequent signer independently signs the file — their signature is added without altering prior signers' signatures",
            type: "numbered",
          },
          {
            text: "Progress tracking shows which allowlisted signers are still pending and which have completed their signature",
            type: "numbered",
          },
          {
            text: "Once all required signers have signed, the document can be sealed — individually or in bulk — to finalize it",
            type: "numbered",
          },
          {
            text: "Verifiers can inspect every individual signer's signature separately in Advanced verification mode",
            type: "numbered",
          },
        ],
        note: "Multi-signature documents preserve every individual signature — sealing finalizes the document but does not merge or discard any signer's signature.",
      },
      {
        title: "Anchoring a Sealed File with Majik Notary",
        description:
          "Once a document is sealed, you can optionally anchor it on Solana Mainnet for an independent, on-chain record of its existence at that point in time:",
        useStepTracker: true,
        steps: [
          {
            text: "Seal the file — Majik Notary requires a sealed file as its input",
            type: "numbered",
          },
          {
            text: "A SHA3-512 seal hash is computed from the sealed file",
            type: "numbered",
          },
          {
            text: "Only the seal hash — never the file itself — is submitted as a Solana Memo transaction on Mainnet",
            type: "numbered",
          },
          {
            text: "Majikah covers the on-chain gas fee through its internal system — no wallet or blockchain account is required from you",
            type: "numbered",
          },
          {
            text: "The resulting transaction reference is stored alongside the file's signature envelope for future verification",
            type: "numbered",
          },
        ],
        note: "Majik Notary is a pay-per-file service (₱50.00 per file, VAT exclusive; processing fees may apply) and is entirely optional — files remain fully verifiable through their own cryptographic signatures without it.",
      },
      {
        title: "Signing Plain Text",
        description: "When you sign plain text or a clipboard snippet:",
        steps: [
          {
            text: "Select your signing account and ensure it's unlocked",
            type: "numbered",
          },
          {
            text: "The text is UTF-8 encoded to bytes and hashed with SHA-256",
            type: "numbered",
          },
          {
            text: "A canonical payload is built with the hash, and both Ed25519 and ML-DSA-87 sign it",
            type: "numbered",
          },
          {
            text: "The envelope is serialized to a base64 string (detached signature) you can share alongside the original text",
            type: "numbered",
          },
        ],
        note: "For text signing, the signature is detached — you share the original text and the base64 signature separately.",
      },
      {
        title: "Verifying a Signed File",
        description:
          "When you verify a file's embedded signature, using either Quick Mode or Advanced Mode:",
        useStepTracker: true,
        steps: [
          {
            text: "The file is scanned for one or more embedded MajikSignature envelopes and each is extracted and parsed",
            type: "numbered",
          },
          {
            text: "The signature is stripped from the file to recover the original bytes, which are re-hashed with SHA-256",
            type: "numbered",
          },
          {
            text: "The recovered hash is compared to the contentHash in each envelope — a mismatch fails verification immediately",
            type: "numbered",
          },
          {
            text: "The canonical payload is rebuilt, and both the Ed25519 and ML-DSA-87 signatures are verified against the signer's public keys",
            type: "numbered",
          },
          {
            text: "If an expiration date (validUntil) is present, it's checked only after both signatures pass — the result is reported as expired rather than invalid",
            type: "numbered",
          },
          {
            text: "If a TSA timestamp token is present, it's independently checked against the timestamp authority's signature",
            type: "numbered",
          },
          {
            text: "If a Majik Notary anchor is present, its on-chain reference can optionally be checked against Solana Mainnet",
            type: "numbered",
          },
        ],
        note: "Verification is fully public — you only need the signer's public keys. No private key, account, or internet connection is ever required for local-timestamp signatures.",
        blocks: [
          {
            type: "statusCard",
            status: "valid",
            title: "VALID",
            subtitle:
              "Drag and drop a signed file to instantly verify authenticity, integrity, and timestamp.",
            checks: [
              "Document is authentic",
              "File is unmodified",
              "All signatures are valid",
              "Trusted timestamp verified",
            ],
            footerLabel: "2 Signatures Found",
          },
        ],
      },
      {
        title: "Verifying a Full Signing Chain",
        description:
          "When you want to verify a file's complete revision history rather than just its latest signature:",
        steps: [
          {
            text: "Load the latest file — by default, it carries the complete chain information and all signatures needed for a self-contained check",
            type: "numbered",
          },
          {
            text: "For a deeper, archive-assisted check, load the earlier revisions from the signing chain as well",
            type: "numbered",
          },
          {
            text: "Majik Signature confirms each revision matches the expected previous file's bytes and signatures",
            type: "numbered",
          },
          {
            text: "The version chain hash is checked at each step, surfacing any gap, substitution, or metadata tampering in the history",
            type: "numbered",
          },
        ],
        note: "Full Chain Verification is most useful when you have access to prior revisions and want confidence in the entire signing history, not just the current file.",
      },
      {
        title: "Verifying Plain Text",
        description:
          "When you verify a detached base64 signature against text:",
        steps: [
          {
            text: "Paste the original text and the base64 signature into Majik Signature",
            type: "numbered",
          },
          {
            text: "The signature is deserialized from base64 and parsed as a MajikSignature JSON envelope",
            type: "numbered",
          },
          {
            text: "The text is UTF-8 encoded and hashed with SHA-256, and the hash is compared to contentHash in the envelope",
            type: "numbered",
          },
          {
            text: "The canonical payload is rebuilt and both signatures are verified — if both pass and the hash matches, the result is valid",
            type: "numbered",
          },
        ],
        note: "For text verification, you must provide the exact original text that was signed — any character change invalidates the signature.",
      },
      {
        title: "Trust Models: Contact vs. Self-Reported",
        description: "Majik Signature supports two verification modes:",
        steps: [
          {
            text: "Contact verification (recommended): choose a known contact from your directory — this is the trusted path, you know who signed it.",
            type: "bullet",
          },
          {
            text: "Self-reported verification (TOFU): use the public keys embedded in the signature envelope itself — trust-on-first-use.",
            type: "bullet",
          },
        ],
        note: "Always cross-check the signerId fingerprint when using self-reported keys. The signature proves control of the private key, not real-world identity.",
      },
    ],
  },

  // ============================================================
  // PRICING — TSA credit packs and Majik Notary as tiered cards.
  // ============================================================
  pricing: {
    title: "Pricing",
    model: "freemium",
    currentStatus:
      "Core signing and verification are **free for all users**, with no limits on local (offline) signatures. The web app and desktop apps are available at no cost. A Majikah account is optional and only required for online features.",
    futureStatus:
      "**Trusted Timestamps (TSA):** every Majikah account includes 5 free Trusted Timestamps every 24 hours, and re-signing the same unmodified file within that window doesn't consume an additional credit. **Majik Notary:** optional on-chain anchoring of a sealed file's seal hash on Solana Mainnet is available for ₱50.00 per file (VAT exclusive; processing fees may apply) — no wallet required, gas fees covered by Majikah. Core signing and verification will always remain free.",
    disclaimer: "Exclusive of VAT and payment processing fees.",
    tiers: [
      {
        id: "majik-notary",
        name: "Majik Notary",
        price: "₱50.00",
        quantityLabel: "Per sealed file",
        description: "Solana Mainnet anchoring — no wallet required",
      },
      {
        id: "tsa-150",
        name: "TSA 150",
        price: "₱99.00",
        quantityLabel: "150 TSA Stamps",
        description: "Starter Pack",
        featured: true,
      },
      {
        id: "tsa-500",
        name: "TSA 500",
        price: "₱299.00",
        quantityLabel: "500 TSA Stamps",
        description: "Value Pack",
      },
      {
        id: "tsa-1000",
        name: "TSA 1000",
        price: "₱499.00",
        quantityLabel: "1000 TSA Stamps",
        description: "Pro Pack",
      },
      {
        id: "tsa-3000",
        name: "TSA 3000",
        price: "₱1,299.00",
        quantityLabel: "3000 TSA Stamps",
        description: "Business Pack",
      },
      {
        id: "tsa-5000",
        name: "TSA 5000",
        price: "₱1,999.00",
        quantityLabel: "5000 TSA Stamps",
        description: "Enterprise Pack",
      },
    ],
  },

  // ============================================================
  // SECURITY — trust badge row up top (slide 8 / 10 bottom bars).
  // ============================================================
  security: {
    title: "Security Considerations",
    badges: [
      {
        icon: "checkCircle",
        label: "100% Transparent",
        description: "Nothing hidden. Everything verifiable.",
      },
      {
        icon: "lock",
        label: "Trust with Confidence",
        description: "Cryptographic proof you can rely on.",
      },
      {
        icon: "shieldCheck",
        label: "Evidence That Matters",
        description: "Stronger proof for audits, contracts, and compliance.",
      },
    ],
    categories: [
      {
        title: "What Majik Signature Guarantees",
        items: [
          {
            label: "Content integrity:",
            description:
              "Any byte change to signed content invalidates both signatures — tamper detection is cryptographically guaranteed",
          },
          {
            label: "Signer binding:",
            description:
              "Signatures are cryptographically bound to a specific Majik Key fingerprint — cannot be transferred between identities",
          },
          {
            label: "Timestamp binding:",
            description:
              "The signing timestamp (local or TSA-issued) is part of the signed payload — cannot be altered after signing",
          },
          {
            label: "Expiration integrity:",
            description:
              "An optional expiration date is part of the signed payload — it cannot be extended, shortened, or removed after signing without invalidating the signature",
          },
          {
            label: "Revision chain integrity:",
            description:
              "A version chain hash binds each revision to the ones before it, so metadata tampering across a file's signing history is detectable even without content byte changes",
          },
          {
            label: "Forgery resistance (classical):",
            description:
              "Ed25519 provides 128-bit classical security — infeasible to forge with current computing power",
          },
          {
            label: "Forgery resistance (post-quantum):",
            description:
              "ML-DSA-87 provides NIST Category 5 post-quantum security — resistant to quantum computer attacks",
          },
          {
            label: "Hybrid downgrade resistance:",
            description:
              "Both algorithms must be broken simultaneously to forge — a break in one is not sufficient",
          },
          {
            label: "Embed integrity:",
            description:
              "File embedding always signs original bytes — the embedding container, and any visual stamp, is never part of what's cryptographically signed",
          },
          {
            label: "Multi-signature independence:",
            description:
              "Each signer's signature on a multi-party document is independently verifiable — one signer's signature cannot be forged or altered by another, even across later revisions",
          },
          {
            label: "Identity anchor integrity:",
            description:
              "When used via Majik Universal ID, the identity anchor signature commits both Ed25519 and ML-DSA-87 over the canonical identity fields — impersonating an identity requires breaking both algorithms simultaneously",
          },
        ],
      },
      {
        title: "What You Must Protect",
        items: [
          {
            label: "Your seed phrase:",
            description:
              "This is the ONLY way to recover your signing identity. If lost, your account and signing capability are permanently inaccessible. Store it securely offline.",
          },
          {
            label: "Your passphrase (or Credential Store device):",
            description:
              "Protects your Argon2id-encrypted private keys, held inside Majik Key, in local storage. Choose a strong, unique passphrase, or rely on Credential Store's biometric/PIN gate if you've opted in.",
          },
          {
            label: "Device security:",
            description:
              "If your device is compromised while your account is unlocked, an attacker could access your private keys and forge signatures in your name.",
          },
          {
            label: "Signer identity mapping:",
            description:
              "The library proves content was signed by a specific key. It does not prove who owns that key in the real world. You must maintain the mapping between signerId (fingerprint) and real-world identity.",
          },
        ],
      },
      {
        title: "What Majik Signature Does Not Protect",
        items: [
          {
            label: "Metadata privacy:",
            description:
              "Signature envelopes are self-contained and include the signer's public keys and fingerprint. This metadata is visible to anyone who can read the signature.",
          },
          {
            label: "Local timestamp accuracy:",
            description:
              "Local timestamps are self-reported by the signer's device — there is no independent authority backing them. Do not rely on local timestamps alone for legal non-repudiation; use a Trusted Timestamp (TSA), or anchor with Majik Notary, when independent proof of signing time matters.",
          },
          {
            label: "Real-world identity:",
            description:
              "Majik Signature proves content was signed by a specific cryptographic key, not by a specific person or organization. For real-world identity binding, use Majik Universal ID with Didit KYC verification.",
          },
          {
            label: "Content privacy:",
            description:
              "Signatures prove authenticity, not confidentiality. Content is not encrypted — use Majik Message for encryption.",
          },
          {
            label: "Re-encoding survival (Tier 2):",
            description:
              "For Tier 2 formats (universal trailer), signatures do not survive re-encoding or re-muxing. Use Tier 1 native-metadata formats where durability matters.",
          },
          {
            label: "On-chain confidentiality (Majik Notary):",
            description:
              "A Majik Notary anchor is a public Solana Memo transaction. The SHA3-512 seal hash it contains is publicly visible on-chain, even though the underlying file is never uploaded.",
          },
        ],
      },
      {
        title: "Post-Quantum Security Guarantees",
        items: [
          {
            label: "NIST-standardized:",
            description:
              "ML-DSA-87 (FIPS-204) is a U.S. government standard for post-quantum digital signatures",
          },
          {
            label: "Lattice-based cryptography:",
            description:
              "Based on mathematical problems (Module Learning With Errors) believed to be hard for both classical and quantum computers",
          },
          {
            label: "Conservative security level:",
            description:
              "ML-DSA-87 provides Category 5 security — strong enough to resist even large-scale quantum computers",
          },
          {
            label: "Future-proof:",
            description:
              "Your signatures created today will remain secure even when quantum computers become mainstream",
          },
          {
            label: "Hybrid approach:",
            description:
              "Even if ML-DSA-87 is unexpectedly broken, Ed25519 still provides 128-bit classical security — and vice versa. Trusted Timestamps use the same hybrid guarantees.",
          },
        ],
      },
      {
        title: "Best Practices",
        items: [
          {
            label: "Always verify signerId:",
            description:
              "After verification, check result.signerId matches an expected fingerprint from your contact list or a trusted source",
          },
          {
            label: "Lock accounts when not signing:",
            description:
              "Keep accounts locked (private keys not in memory) except when actively signing",
          },
          {
            label: "Use contact verification over self-reported:",
            description:
              "Whenever possible, verify against a known contact rather than using TOFU mode",
          },
          {
            label:
              "Use Trusted Timestamps or Majik Notary for legal or compliance needs:",
            description:
              "If independent proof of signing time matters, request a TSA timestamp, or anchor the sealed file on Solana Mainnet with Majik Notary, rather than relying on a local timestamp alone",
          },
          {
            label: "Set expiration dates for time-sensitive signatures:",
            description:
              "For contracts, approvals, credentials, or licenses that shouldn't remain valid indefinitely, set a validUntil expiration date at signing time",
          },
          {
            label: "Preserve original file bytes:",
            description:
              "Do not transcode, compress, or re-encode files between signing and verification",
          },
          {
            label: "Keep prior revisions when possible:",
            description:
              "Retaining earlier revisions of a file enables deeper, archive-assisted Full Chain Verification, beyond the self-contained check available from the latest file alone",
          },
          {
            label: "Cross-check TOFU results:",
            description:
              "If using self-reported keys (TOFU), cross-check the signerId fingerprint against a known source before trusting the result",
          },
          {
            label: "Check signer_registered when using Majik Universal ID:",
            description:
              "When verifyContent() returns a result, always confirm result.signer_registered === true and check result.signer_fingerprint against majikId.signingKey.fingerprint before trusting the result",
          },
        ],
      },
    ],
  },

  useCases: [
    {
      title: "Content Creators & Artists",
      icon: "paintBrush",
      description:
        "Sign your music, art, videos, or documents to prove authorship. The signature embeds directly in the file and survives distribution. Buyers, galleries, and fans can verify the work came from you without needing to contact you. Add a visual stamp for a professional, at-a-glance mark of authenticity, and optionally anchor a sealed piece on Solana Mainnet with Majik Notary for independent proof of existence. Perfect for NFT alternatives, portfolio protection, and establishing provenance.",
    },
    {
      title: "Legal & Compliance",
      icon: "scales",
      description:
        "Sign contracts, invoices, records, or legal documents with hybrid post-quantum signatures. Any modification invalidates the signature — providing tamper-evident audit trails. Add Trusted Timestamps (TSA) or a Majik Notary blockchain anchor for independently verifiable proof of signing time, set expiration dates on time-sensitive agreements, and use visual stamps for a familiar, professional signing mark. Perfect for regulatory compliance, contract signing, or any situation requiring verified document integrity.",
    },
    {
      title: "Multi-Party Contracts & Approvals",
      icon: "users",
      description:
        "Route a document through several signers with a signing allowlist and track who has signed and who is still pending. Every signer's signature is independently verifiable — and now survives later revisions, such as an added stamp or annotation — while file sealing finalizes the document once all required approvals are in. Ideal for contracts, internal approvals, and any workflow requiring multiple independent sign-offs.",
    },
    {
      title: "Journalists & Investigators",
      icon: "camera",
      description:
        "Sign raw footage, photos, or documents at the scene to certify they are authentic originals and have not been deepfaked or edited. The signature embeds in the MP4, JPEG, or PDF metadata and can be verified by news organizations or investigators. Full Chain Verification lets a later reviewer confirm the entire chain of edits and stamps applied since the original capture. Combats synthetic media and provides chain-of-custody.",
    },
    {
      title: "Software Distribution",
      icon: "package",
      description:
        "Sign release artifacts (binaries, installers, packages, source code archives) to prove they originated from you. Batch sign entire release folders or ZIP archives at once. Users verify before installation to detect supply-chain attacks or malware injection. Post-quantum signatures resist future quantum threats to code signing.",
    },
    {
      title: "API Payload Signing",
      icon: "code",
      description:
        "Sign JSON API responses or requests for non-repudiation. The signature proves the payload originated from a specific server or client and has not been altered in transit. Works alongside TLS for defense-in-depth.",
    },
    {
      title: "Academic & Research Integrity",
      icon: "graduationCap",
      description:
        "Sign research datasets, papers, or lab records to prove they have not been modified after publication or peer review. Batch sign large datasets or ZIP archives of records at once, and anchor a sealed dataset with Majik Notary for an independent, publicly verifiable record of when it was finalized. Provides cryptographic proof of data integrity for reproducibility and fraud prevention.",
    },
    {
      title: "Healthcare & Medical Records",
      icon: "heartbeat",
      description:
        "Sign medical images, patient records, or diagnostic reports to ensure they have not been tampered with. Post-quantum signatures ensure long-term integrity for records that must remain secure for decades.",
    },
    {
      title: "Time-Sensitive Credentials & Licenses",
      icon: "clock",
      description:
        "Issue certificates, credentials, memberships, or licenses with a built-in expiration date. Anyone verifying the signature can immediately see whether it's still within its validity window or has expired, without needing to check a separate revocation list.",
    },
    {
      title: "Verified Identity Content Provenance via Majik Universal ID",
      icon: "fingerprint",
      description:
        "When integrated with Majik Universal ID, Majik Signature enables identity-bound content provenance — content is not just signed by a cryptographic key, but by a KYC-verified identity. All signContent() and signFile() calls on a MajikUniversalID produce full hybrid Ed25519 + ML-DSA-87 signatures bound to the identity's verified MajikKey.",
    },
    {
      title: "Anyone Seeking Long-Term Content Integrity",
      icon: "hourglass",
      description:
        "If your content needs to remain verifiably authentic for years or decades — and you want quantum-safe protection today — Majik Signature is built for you. Your signatures will remain secure even in 2040.",
    },
  ],

  gettingStarted: {
    title: "Getting Started",
    steps: [
      {
        title: "1. Access Majik Signature",
        items: [
          "Open the web app at https://signature.majikah.solutions",
          "Or download the Windows desktop app from the Microsoft Store",
        ],
      },
      {
        title: "2. Create a Majik Key Account",
        items: [
          "Navigate to the Accounts tab",
          "Click the + icon to create a new account",
          "Generate a new BIP-39 mnemonic — 12/24-word or 24-word, in any official BIP-39 language — or import an existing one",
          "Set a strong passphrase to protect your private keys, or opt in to Credential Store passwordless unlock",
          "Save your mnemonic backup — this is the ONLY way to recover your account",
          "New users can skip onboarding and go straight into the app if preferred",
        ],
      },
      {
        title: "3. Sign Your First File",
        items: [
          "Navigate to the Sign tab",
          "Choose Signing Wizard for a guided flow, or Advanced Mode for full control",
          "Drag-and-drop any file (PDF, image, audio, video, document, etc.)",
          "Select your signing account from the dropdown",
          "Optionally add a visual stamp (PDF, DOCX, XLSX), set a signature expiration date, or request a Trusted Timestamp",
          "Click Sign",
          "Download the signed file — signature is embedded in the file's metadata",
        ],
      },
      {
        title: "4. Sign Plain Text",
        items: [
          "Navigate to the Sign tab",
          "Switch to Text mode",
          "Paste or type your text",
          "Select your signing account",
          "Click Sign",
          "Copy the base64 signature — share it alongside the original text",
        ],
      },
      {
        title: "5. Verify a Signed File",
        items: [
          "Navigate to the Verify tab",
          "Use Quick Mode for a fast drag-and-drop check, or Advanced Mode for detailed inspection",
          "Drag-and-drop the signed file",
          "Optionally select a known contact to verify against (or use self-reported keys)",
          "Click Verify",
          "See real-time verification status with algorithm-level checks (SHA-256, Ed25519, ML-DSA-87), expiration status, and TSA/Notary proof for each signer",
          "If valid: content is authentic and unmodified",
          "If invalid: content has been tampered with or signature is forged",
          "If expired: content is authentic, but the signature's validity period has passed",
        ],
      },
      {
        title: "6. Verify Plain Text",
        items: [
          "Navigate to the Verify tab",
          "Switch to Text mode",
          "Paste the original text in the first box",
          "Paste the base64 signature in the second box",
          "Optionally select a contact to verify against",
          "Click Verify",
          "See verification result",
        ],
      },
      {
        title: "7. Set Up Multi-Party Signing (Optional)",
        items: [
          "From the Sign tab, choose to add multiple signers to a document",
          "Choose an open signing mode or set a signing allowlist for restricted signing",
          "Share the file with each intended signer",
          "Track pending and completed signatures from the document's progress view",
          "Seal the document individually or in bulk once all required signers have signed",
        ],
      },
      {
        title: "8. Set Up a Hot Folder (Optional)",
        items: [
          "Navigate to the Hot Folders tab",
          "Choose a folder to monitor and assign it a custom icon",
          "Incoming files are automatically verified and sorted into Passed or Failed subfolders",
          "Automatic signing of incoming files is on the roadmap — Hot Folders automate verification today",
        ],
      },
      {
        title: "9. Anchor a Sealed File with Majik Notary (Optional)",
        items: [
          "Seal the file you want to anchor",
          "From the sealed file's options, choose Anchor with Majik Notary",
          "Confirm the ₱50.00 per-file fee (VAT exclusive; processing fees may apply)",
          "No crypto wallet or blockchain account setup is required — Majikah handles the on-chain transaction",
          "The Solana Mainnet transaction reference is attached to the file for future verification",
        ],
      },
      {
        title: "10. Verify a Full Signing Chain (Optional)",
        items: [
          "Navigate to the Verify tab and switch to Advanced Mode",
          "Load the latest file — it carries the complete chain information and all signatures by default",
          "For a deeper check, also load one or more earlier revisions from the signing chain",
          "Review the per-revision verification result, confirming each revision matches its expected predecessor",
        ],
      },
      {
        title: "11. Sign In to Majikah for Online Features (Optional)",
        items: [
          "Open the Majikah panel from within the app",
          "Sign in or create a Majikah account",
          "Use your 5 free Trusted Timestamps every 24 hours, or purchase additional TSA credit packs",
          "Local (offline) signing and verification continue to work without a Majikah account",
        ],
      },
      {
        title: "12. Use as a Peer Dependency (Majik Universal ID)",
        items: [
          "Install via npm: npm install @majikah/majik-signature",
          "Majik Universal ID automatically uses Majik Signature for all signing and verification",
          "All signContent(), signFile(), verifyContent(), and verifyFile() calls on a MajikUniversalID delegate to this library",
          "No additional configuration required — the bound MajikKey provides all signing keys",
        ],
      },
      {
        title: "13. Manage Contacts (Optional)",
        items: [
          "Navigate to the Majikah tab",
          "Import contacts via invite keys (ask someone to share their Majik Key invite key)",
          "Contacts appear in the Verify tab's contact selector",
          "Verify signatures against known contacts for trusted verification",
        ],
      },
    ],
  },

  roadmap: {
    title: "Roadmap & Future Features",
    description:
      "Majik Signature is under active development. Planned features include:",
    items: [
      {
        label: "macOS and Linux desktop apps",
        description:
          "Tauri-based desktop applications for macOS and Linux, matching the existing Windows and web experience",
        status: "coming-soon",
      },
      {
        label: "Chrome extension",
        description:
          "Sign and verify files directly from Chrome — detect signatures on any webpage",
        status: "planned",
      },
      {
        label: "Hot Folder auto-signing",
        description:
          "Extend Hot Folders beyond automatic verification to automatic signing of incoming files",
        status: "planned",
      },
      {
        label: "Signature audit trail export",
        description:
          "Export a CSV or JSON report of all signatures you've created or verified",
        status: "planned",
      },
      {
        label: "Mobile apps (iOS/Android)",
        description:
          "React Native mobile apps for signing and verifying on the go",
        status: "planned",
      },
      {
        label: "Advanced verification modes",
        description:
          "Verify against threshold-signature policies and verify entire directory trees at once, building on the multi-signature and Full Chain Verification support already shipped in v0.11.0",
        status: "planned",
      },
      {
        label: "CLI / MCP beyond Windows",
        description:
          "Extend the mjksig CLI and mjksig-mcp MCP server beyond the current Windows-only, experimental release",
        status: "planned",
      },
      {
        label: "On-chain anchor verification in-app",
        description:
          "Look up and display a Majik Notary anchor's Solana Mainnet transaction status directly within the verification flow",
        status: "planned",
      },
    ],
  },

  ecosystem: {
    title: "Part of the Majikah Ecosystem",
    paragraphs: [
      "Majik Signature is part of the Majikah system — a suite of privacy-focused, user-controlled tools designed to give individuals full ownership of their digital identity and content authenticity.",
      "All Majikah products share the same core principles: cryptographic identity via Majik Key, zero-knowledge architecture, post-quantum security, and user sovereignty over personal information and data.",
      "Majik Signature is the signing engine for Majik Universal ID — every identity record is anchored with a hybrid Ed25519 + ML-DSA-87 identity signature, and all content and file signing through a MajikUniversalID delegates directly to this library. If you need identity-bound content provenance with KYC verification, combine Majik Signature with Majik Universal ID.",
      "Majik SLink extends the same signing model to live URLs — websites, social profiles, repositories, and other public pages — with claim types (Ownership, Attribution, Reference) verified independently at slink.majikah.solutions.",
      "Majik Notary adds an optional, independent layer of proof on top of the same signed and sealed files — anchoring only a SHA3-512 seal hash on Solana Mainnet, without a wallet, blockchain account, or gas fees on your end.",
      "Trusted Timestamps (TSA) and other online features are accessed through the same Majikah account used across the ecosystem — one optional sign-in unlocks online features across Majikah products without changing how local, offline signing and verification work.",
      "Use Majik Signature alongside Majik Message for a complete post-quantum communication and authentication stack — encrypt your messages with Majik Message, sign your files with Majik Signature, all using the same seed phrase identity.",
    ],
  },

  // ============================================================
  // CTA — full-bleed statement moment (slide 3 style), with a
  // subtle 3D accent behind the copy.
  // ============================================================
  cta: {
    title: "Ready to Sign with Post-Quantum Security?",
    description:
      "Start signing, verifying, and anchoring files with hybrid Ed25519 + ML-DSA-87 signatures today:",
    downloads: [
      { platform: "Web App", url: "https://signature.majikah.solutions" },
      {
        platform: "Microsoft Store",
        url: "https://apps.microsoft.com/detail/9pl9g3xzvd1x",
      },
      {
        platform: "GitHub Releases",
        url: "https://github.com/Majikah/majik-signature-desktop-releases/releases",
      },
      {
        platform: "npm (@majikah/majik-signature)",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
      },
    ],
    closingStatement: "Quantum-safe content authentication, available today.",
    scene3d: {
      type: "scene3d",
      sceneKind: "lockPulse",
      caption: "Your files stay on your device — privacy by design.",
      intensity: "subtle",
    },
  },
  platforms: [
    { id: "webapp", url: "https://signature.majikah.solutions" },
    {
      id: "windows",
      url: "https://apps.microsoft.com/detail/9pl9g3xzvd1x",
    },
  ],
};

export default JSON_PRODUCT_MAJIK_SIGNATURE;
